import { useState } from 'react'
import { toast } from 'react-toastify'
import { Alert, AlertType } from 'components/Alert/Alert'
import { Container, ContainerContent } from 'components/Containers/Containers'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Button, Flex, Tabs as PillTab, TabsContainer, ColorVariant, colors } from 'styling/GlobalStyles'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { IKitchenSite } from 'types/kitchen/kitchenSite.interface'
import { ProductionStatsRow } from '../Kitchen.styled'

interface IProps {
  kitchens: IKitchenSite[]
}

export const ProductionTab = ({ kitchens }: IProps) => {
  const [kitchenId, setKitchenId] = useState(1)
  const kitchen = kitchens.find((k) => k.id === kitchenId)!

  return (
    <Flex column gap='1rem' width='100%'>
      <Flex gap='0.5rem' center width='100%' between>
        <TabsContainer>
          {kitchens.map((k) => (
            <PillTab key={k.id} active={kitchenId === k.id} onClick={() => setKitchenId(k.id)}>
              {k.name}
            </PillTab>
          ))}
        </TabsContainer>
        <OtherText grey>
          Operational day: <BodyText weight='700' style={{ display: 'inline' }}>Fri 20/2/2028</BodyText>
        </OtherText>
      </Flex>
      {kitchenId === 1 ? <MainKitchenProduction /> : <ContractorProduction kitchen={kitchen} />}
    </Flex>
  )
}

const MainKitchenProduction = () => {
  const breakfastStats = [
    ['Ordered', 98],
    ['Served', 96],
    ['Plate waste', 2],
    ['Standby used', 2],
    ['Window COGS', '$117.20'],
    ['Avg cost / meal', '$1.20'],
  ]

  const issueRows = [
    { id: 'TX-88121', ingredient: 'Rice', qty: '13.7 kg', batches: 'B-204 (exp 12/3)' },
    { id: 'TX-88122', ingredient: 'Chicken', qty: '11.4 kg', batches: 'B-211 (exp 23/2)' },
    { id: 'TX-88123', ingredient: 'Beans', qty: '3.5 kg', batches: 'B-187 (exp 20/4)' },
    { id: 'TX-88124', ingredient: 'Onion', qty: '2.8 kg', batches: 'B-195 (exp 4/3)' },
    { id: 'TX-88125', ingredient: 'Cooking Oil', qty: '1.9 L', batches: 'B-190 (exp 30/5)' },
    { id: 'TX-88126', ingredient: 'Sandwich Pack (standby SKU)', qty: '2 pcs', batches: 'B-201' },
  ]

  const pullRows = [
    { id: 1, ingredient: 'Rice', required: '17.3 kg', available: '31.3 kg', fefo: 'B-204 (exp 12/3)', ok: true },
    { id: 2, ingredient: 'Chicken', required: '14.4 kg', available: '2.6 kg', fefo: 'B-211 (exp 23/2)', ok: false, short: 'short 11.8 kg' },
    { id: 3, ingredient: 'Pasta', required: '2.9 kg', available: '14 kg', fefo: 'B-208 (exp 22/2) first', ok: true },
    { id: 4, ingredient: 'Tomato Sauce', required: '1.9 L', available: '9 L', fefo: 'B-199 (exp 26/2)', ok: true },
    { id: 5, ingredient: 'Onion', required: '3.4 kg', available: '9.2 kg', fefo: 'B-195 (exp 4/3)', ok: true },
    { id: 6, ingredient: 'Cooking Oil', required: '2.1 L', available: '16.1 L', fefo: 'B-190 (exp 30/5)', ok: true },
  ]

  const dishRows = [
    { id: 1, dish: 'Jollof Rice & Chicken', portions: 96, packout: 14, norm: 'Rice 180g · Chicken 150g · Onion 30g · Oil 20ml' },
    { id: 2, dish: 'Vegetable Pasta', portions: 24, packout: 3, norm: 'Pasta 120g · Sauce 80ml · Onion 20g · Oil 10ml' },
  ]

  const issueColumns: IHonestTableColumn<typeof issueRows[0]>[] = [
    { id: 'id', header: 'Issue TX', cell: (r) => <BodyText>{r.id}</BodyText> },
    { id: 'ingredient', header: 'Ingredient', cell: (r) => <BodyText>{r.ingredient}</BodyText> },
    { id: 'qty', header: 'Quantity', cell: (r) => <BodyText>{r.qty}</BodyText> },
    { id: 'batches', header: 'Batches consumed (FEFO)', cell: (r) => <OtherText grey>{r.batches}</OtherText> },
  ]

  const pullColumns: IHonestTableColumn<typeof pullRows[0]>[] = [
    { id: 'ingredient', header: 'Ingredient', cell: (r) => <BodyText>{r.ingredient}</BodyText> },
    { id: 'required', header: 'Required (total)', cell: (r) => <BodyText>{r.required}</BodyText> },
    { id: 'available', header: 'Available', cell: (r) => <BodyText>{r.available}</BodyText> },
    { id: 'fefo', header: 'Pull from (FEFO)', cell: (r) => <OtherText grey>{r.fefo}</OtherText> },
    {
      id: 'status',
      header: 'Status',
      cell: (r) =>
        r.ok ? (
          <StatusPill status='success'>OK</StatusPill>
        ) : (
          <StatusPill status='danger'>{r.short}</StatusPill>
        ),
    },
  ]

  const dishColumns: IHonestTableColumn<typeof dishRows[0]>[] = [
    { id: 'dish', header: 'Dish', cell: (r) => <BodyText>{r.dish}</BodyText> },
    { id: 'portions', header: 'Portions', cell: (r) => <BodyText weight='700'>{r.portions}</BodyText> },
    { id: 'packout', header: 'of which Packout', cell: (r) => <BodyText>{r.packout}</BodyText> },
    { id: 'norm', header: 'Per-portion normative (v. active today)', cell: (r) => <OtherText grey>{r.norm}</OtherText> },
  ]

  return (
    <>
      <Container>
        <ContainerContent gap='0.75rem'>
          <Flex gap='0.625rem' center wrap>
            <TitleText weight='700'>Breakfast</TitleText>
            <StatusPill status='success'>Consumption posted</StatusPill>
            <Flex style={{ flex: 1 }} />
            <OtherText grey>Run CR-2041 · ref FL window FL-1182 · idempotent</OtherText>
          </Flex>
          <ProductionStatsRow>
            {breakfastStats.map(([l, v]) => (
              <MetricCard key={l as string} value={v as string | number} label={l as string} accentColor={colors.orange} />
            ))}
          </ProductionStatsRow>
          <OtherText grey>
            Menu as published: Jollof Rice & Chicken ×76 · Beans Stew ×22 — explosion on ORDERED counts (cooking consumes; serving does not)
          </OtherText>
          <HonestTable columns={issueColumns} data={issueRows} keyField='id' />
        </ContainerContent>
      </Container>

      <Container>
        <ContainerContent gap='0.75rem'>
          <Flex gap='0.625rem' center wrap>
            <TitleText weight='700'>Lunch</TitleText>
            <StatusPill status='accent'>Ordered — awaiting kitchen confirmation</StatusPill>
            <Flex style={{ flex: 1 }} />
            <Button orangeBorder onClick={() => toast.info('Prep sheet sent to printer')}>
              Print Prep Sheet
            </Button>
          </Flex>
          <BodyText weight='700' grey>
            PULL LIST — store totals for this window (FEFO)
          </BodyText>
          <HonestTable columns={pullColumns} data={pullRows} keyField='id' />
          <Alert
            title='Chicken short 11.8 kg vs locked order'
            description='Options: Inventory transfer from central warehouse (lead: same-day), or portion adjustment recorded at cycle count. Posting will record ledger reality; variance is surfaced, never hidden.'
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
          <BodyText weight='700' grey>
            PER DISH — for the cooks
          </BodyText>
          <HonestTable columns={dishColumns} data={dishRows} keyField='id' />
        </ContainerContent>
      </Container>

      <Container>
        <ContainerContent gap='0.75rem'>
          <Flex gap='0.625rem' center>
            <TitleText weight='700'>Dinner</TitleText>
            <StatusPill status='accent'>Pre cut-off (15:30) — counts still adjusting</StatusPill>
          </Flex>
          <Alert
            title='Menu not published for this window'
            description='If Dinner finalizes without a published menu, consumption posting FAILS (no tag-set → dish mapping to explode) and raises a Critical exception. Complete and publish in Menu Planner →'
            type={AlertType.WARNING}
            colorVariant={ColorVariant.PRIMARY}
          />
        </ContainerContent>
      </Container>
    </>
  )
}

const ContractorProduction = ({ kitchen }: { kitchen: IKitchenSite }) => {
  const orderRows = [
    {
      id: 'CO-0412',
      window: 'Breakfast',
      lines: 'Omelette (Halal line) · Halal ×10 · Hausa koko · Regular ×30 · Vegetarian ×7',
      standby: '1',
      waste: '0',
      status: 'Finalized',
      action: 'export',
    },
    {
      id: 'CO-0413',
      window: 'Lunch',
      lines: 'Halal Beef Stew · Halal ×12 · Regular ×38 · Vegetable Pasta · Vegetarian ×8',
      standby: '—',
      waste: '—',
      status: 'Awaiting finalization',
      action: 'preview',
    },
    {
      id: '—',
      window: 'Dinner',
      lines: 'Generates at cut-off (15:30) from published menu + locked counts',
      standby: '—',
      waste: '—',
      status: 'Pre cut-off',
      action: '',
    },
  ]

  const columns: IHonestTableColumn<typeof orderRows[0]>[] = [
    { id: 'id', header: 'Order', cell: (r) => <BodyText>{r.id}</BodyText> },
    { id: 'window', header: 'Window', cell: (r) => <BodyText>{r.window}</BodyText> },
    { id: 'lines', header: 'Lines (dish · tag set · qty)', cell: (r) => <OtherText grey={r.id === '—'}>{r.lines}</OtherText> },
    { id: 'standby', header: 'Standby used', cell: (r) => <BodyText>{r.standby}</BodyText> },
    { id: 'waste', header: 'Expected waste', cell: (r) => <BodyText>{r.waste}</BodyText> },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => (
        <StatusPill status={r.status === 'Finalized' ? 'success' : 'accent'}>{r.status}</StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <Flex justend width='100%'>
          {r.action === 'export' ? (
            <Button orangeBorder onClick={() => toast.success(`${r.id} exported (PDF) — settlement evidence`)}>
              Export
            </Button>
          ) : r.action === 'preview' ? (
            <Button orangeBorder>Order preview</Button>
          ) : null}
        </Flex>
      ),
    },
  ]

  return (
    <Container>
      <ContainerContent gap='0.75rem'>
        <TitleText weight='700'>
          {kitchen.name} — Contractor flow ({kitchen.contractor}) · orders only, no client stock
        </TitleText>
        <HonestTable columns={columns} data={orderRows} keyField='id' />
        <OtherText grey>
          Contractor Orders are immutable, generated from finalized Food Logistics windows. No normative explosion, no deduction, no client cost data in any contractor-facing artifact.
        </OtherText>
      </ContainerContent>
    </Container>
  )
}
